/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import { classes, Container } from '@adobe-commerce/elsie/lib';
import { Incrementer } from '@adobe-commerce/elsie/components';
import { events } from '@adobe-commerce/event-bus';
import { useText } from '@adobe-commerce/elsie/i18n';
import { ProductModel } from '@/pdp/data/models';
import {
  setProductConfigurationValues,
  getFetchedProductData,
} from '@/pdp/api';
import {
  setUrlParams,
  getValidatedQuantityFromUrl,
} from '@/pdp/lib/url-params';
import { config } from '@/pdp/api/initialize';

export interface ProductQuantityProps extends HTMLAttributes<HTMLDivElement> {
  scope?: string;
  onValue?: (quantity: number) => void;
}

export const ProductQuantity: Container<
  ProductQuantityProps,
  ProductModel | null
> = ({ initialData = null, scope, onValue, children, ...props }) => {
  const IncrementerLabel = useText('PDP.Product.Incrementer.label').label;

  const [data, setData] = useState<ProductModel | null>(initialData);

  const [current, setCurrent] = useState(() => getValidatedQuantityFromUrl(1)); // Default quantity to 1

  useEffect(() => {
    const dataEvent = events.on('pdp/data', setData, { scope });

    const valuesEvent = events.on(
      'pdp/values',
      ({ quantity }) => {
        setCurrent(quantity);
      },
      { eager: true, scope }
    );

    return () => {
      dataEvent?.off();
      valuesEvent?.off();
    };
  }, [scope]);

  const handleOnValue = (value: string) => {
    const quantity = parseInt(value, 10);
    setProductConfigurationValues((prev) => ({ ...prev, quantity }), { scope });

    // Update URL parameters if persistURLParams is enabled
    const { persistURLParams } = config.getConfig() ?? {};
    if (persistURLParams) {
      setUrlParams({ quantity });
    }

    onValue?.(quantity);
  };

  if (!data) return null;

  return (
    <Incrementer
      key="quantity"
      name="quantity"
      defaultValue={String(current)}
      value={String(current)}
      min={1}
      aria-label={IncrementerLabel}
      onValue={handleOnValue}
      {...props}
      className={classes(['pdp-quantity', props.className])}
    />
  );
};

ProductQuantity.getInitialData = getFetchedProductData;
